import React, { useState, useEffect } from 'react';
import {
  Card,
  Row,
  Col,
  Statistic,
  Table,
  Select,
  Button,
  Progress,
  Tag,
  Modal,
  Form,
  InputNumber,
  Space,
  message,
  Tooltip,
  Alert,
  Empty,
  Switch,
  Tabs
} from 'antd';
import {
  ThunderboltOutlined,
  FireOutlined,
  ExperimentOutlined,
  ReloadOutlined,
  SettingOutlined,
  TrophyOutlined,
  RiseOutlined,
  FallOutlined
} from '@ant-design/icons';
import { Line, Column, Pie } from '@ant-design/plots';
import { environmentService, EnergyData, StatsQuery } from '../../../services/environmentService';
import type { ColumnsType } from 'antd/es/table';

const { Option } = Select;
const { TabPane } = Tabs;

interface EnergyManagementProps {
  onRefresh?: () => void;
}

const EnergyManagement: React.FC<EnergyManagementProps> = ({ onRefresh }) => {
  const [loading, setLoading] = useState(false);
  const [energyData, setEnergyData] = useState<EnergyData[]>([]);
  const [filteredData, setFilteredData] = useState<EnergyData[]>([]);
  const [statsData, setStatsData] = useState<any>(null);
  const [trendsData, setTrendsData] = useState<any[]>([]);
  const [alertsData, setAlertsData] = useState<any[]>([]);
  const [thresholdModalVisible, setThresholdModalVisible] = useState(false);
  const [selectedDevice, setSelectedDevice] = useState<EnergyData | null>(null);
  const [aiOptimizing, setAiOptimizing] = useState(false);
  const [filters, setFilters] = useState<StatsQuery>({
    area: '',
    floor: undefined,
    period: 'daily'
  });
  const [form] = Form.useForm();

  // 載入能源數據
  const loadEnergyData = async () => {
    try {
      setLoading(true);
      const [data, trends, alerts] = await Promise.all([
        environmentService.getEnergyData(filters),
        environmentService.getEnergyTrends(filters),
        environmentService.getAlerts('energy')
      ]);
      setEnergyData(data);
      setFilteredData(data);
      setStatsData(trends); // 使用trends作為stats數據
      setTrendsData(trends);
      setAlertsData(alerts);
    } catch (error) {
      console.error('載入能源數據失敗:', error);
      message.error('載入能源數據失敗');
    } finally {
      setLoading(false);
    }
  };

  // AI 能源優化
  const handleAIOptimization = async () => {
    try {
      setAiOptimizing(true);
      await environmentService.optimizeEnergy();
      message.success('AI 能源優化已啟動');
      await loadEnergyData();
      onRefresh?.();
    } catch (error) {
      console.error('AI 能源優化失敗:', error);
      message.error('AI 能源優化失敗');
    } finally {
      setAiOptimizing(false);
    }
  };

  // 設置能源警報閾值
  const updateThresholds = async (values: any) => {
    if (!selectedDevice) return;

    try {
      await environmentService.setEnergyThresholds(selectedDevice.systemId, values);
      message.success('能源警報閾值設置成功');
      setThresholdModalVisible(false);
      setSelectedDevice(null);
      form.resetFields();
      await loadEnergyData();
      onRefresh?.();
    } catch (error) {
      console.error('設置能源警報閾值失敗:', error);
      message.error('設置能源警報閾值失敗');
    }
  };

  // 獲取能源效率等級
  const getEfficiencyLevel = (efficiency: number) => {
    if (efficiency >= 90) return { level: 'A+', color: '#52c41a', desc: '極高效率' };
    if (efficiency >= 80) return { level: 'A', color: '#73d13d', desc: '高效率' };
    if (efficiency >= 70) return { level: 'B', color: '#faad14', desc: '良好效率' };
    if (efficiency >= 60) return { level: 'C', color: '#fa8c16', desc: '一般效率' };
    return { level: 'D', color: '#f5222d', desc: '低效率' };
  };

  // 獲取設備狀態標籤
  const getStatusTag = (status: string) => {
    const config = {
      online: { color: 'green', text: '在線' },
      offline: { color: 'red', text: '離線' },
      maintenance: { color: 'orange', text: '維護中' },
      error: { color: 'red', text: '故障' }
    };
    const { color, text } = config[status as keyof typeof config] || config.offline;
    return <Tag color={color}>{text}</Tag>;
  };

  // 獲取設備類型圖標
  const getDeviceIcon = (type: string) => {
    const icons = {
      lighting: <ThunderboltOutlined style={{ color: '#faad14' }} />,
      hvac: <FireOutlined style={{ color: '#1890ff' }} />,
      elevator: <RiseOutlined style={{ color: '#722ed1' }} />,
      security: <ExperimentOutlined style={{ color: '#52c41a' }} />
    };
    return icons[type as keyof typeof icons] || <ThunderboltOutlined />;
  };

  // 表格列定義
  const columns: ColumnsType<EnergyData> = [
    {
      title: '設備',
      key: 'device',
      width: 200,
      render: (_, record) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {getDeviceIcon('energy')}
          <div>
            <div style={{ fontWeight: 'bold' }}>{record.systemId}</div>
            <div style={{ fontSize: 12, color: '#666' }}>能源系統</div>
          </div>
        </div>
      )
    },
    {
      title: '位置',
      key: 'location',
      width: 150,
      render: (_, record) => (
        <span>
          {record.location.area} - {record.location.floor}F
          {record.location.zone && ` - ${record.location.zone}`}
        </span>
      )
    },
    {
      title: '當前功率 (kW)',
      key: 'currentPower',
      width: 120,
      render: (_, record) => (
        <Tooltip title={`當前功率: ${record.consumption.current} kW`}>
          <span style={{ 
            color: record.consumption.current > 100 ? '#f5222d' : '#1890ff',
            fontWeight: 'bold' 
          }}>
            {record.consumption.current}
          </span>
        </Tooltip>
      ),
      sorter: (a, b) => a.consumption.current - b.consumption.current
    },
    {
      title: '日消耗 (kWh)',
      key: 'dailyConsumption',
      width: 120,
      render: (_, record) => (
        <div>
          <div>{record.consumption.daily}</div>
          <div style={{ fontSize: 12, color: '#666' }}>
            月累計: {record.consumption.monthly}
          </div>
        </div>
      ),
      sorter: (a, b) => a.consumption.daily - b.consumption.daily
    },
    {
      title: '效率等級',
      key: 'efficiency',
      width: 100,
      render: (_, record) => {
        const { level, color, desc } = getEfficiencyLevel(record.efficiency.rating);
        return (
          <Tooltip title={desc}>
            <Tag color={color} style={{ fontWeight: 'bold' }}>
              {level}
            </Tag>
          </Tooltip>
        );
      },
      filters: [
        { text: 'A+', value: 'A+' },
        { text: 'A', value: 'A' },
        { text: 'B', value: 'B' },
        { text: 'C', value: 'C' },
        { text: 'D', value: 'D' }
      ],
      onFilter: (value, record) => {
        const { level } = getEfficiencyLevel(record.efficiency.rating);
        return level === value;
      }
    },
    {
      title: '成本 (元)',
      key: 'cost',
      width: 120,
      render: (_, record) => (
        <div>
          <div>日: ¥{record.cost.daily}</div>
          <div style={{ fontSize: 12, color: '#666' }}>
            月: ¥{record.cost.monthly}
          </div>
        </div>
      ),
      sorter: (a, b) => a.cost.daily - b.cost.daily
    },
    {
      title: '狀態',
      key: 'status',
      width: 80,
      render: (_, record) => getStatusTag('online'), // 默認為在線狀態
      filters: [
        { text: '在線', value: 'online' },
        { text: '離線', value: 'offline' },
        { text: '維護中', value: 'maintenance' },
        { text: '故障', value: 'error' }
      ],
      onFilter: (value, record) => value === 'online' // 默認過濾為在線
    },
    {
      title: '更新時間',
      dataIndex: 'timestamp',
      key: 'timestamp',
      width: 160,
      render: (timestamp: string) => new Date(timestamp).toLocaleString('zh-TW'),
      sorter: (a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime(),
      defaultSortOrder: 'descend'
    },
    {
      title: '操作',
      key: 'actions',
      width: 120,
      render: (_, record) => (
        <Space>
          <Button
            size="small"
            icon={<SettingOutlined />}
            onClick={() => {
              setSelectedDevice(record);
              form.setFieldsValue({
                dailyLimit: 100, // 使用默認值
                monthlyLimit: 3000, // 使用默認值
                efficiencyMin: 60 // 使用默認值
              });
              setThresholdModalVisible(true);
            }}
          >
            設置
          </Button>
        </Space>
      )
    }
  ];

  // 準備趨勢圖表數據
  const prepareTrendData = () => {
    if (!trendsData.length) return [];
    
    return trendsData.map(item => ({
      time: new Date(item.timestamp).toLocaleDateString('zh-TW'),
      consumption: item.consumption,
      cost: item.cost,
      efficiency: item.efficiency,
      category: item.category || '總計'
    }));
  };

  // 準備設備類型分布數據
  const prepareDeviceTypeData = () => {
    if (!energyData.length) return [];
    
    const typeStats = energyData.reduce((acc, item) => {
      const type = '能源系統'; // 使用固定類型
      if (!acc[type]) {
        acc[type] = { type, consumption: 0, cost: 0, count: 0 };
      }
      acc[type].consumption += item.consumption.daily;
      acc[type].cost += item.cost.daily;
      acc[type].count += 1;
      return acc;
    }, {} as Record<string, any>);

    return Object.values(typeStats);
  };

  // 計算統計數據
  const calculateStats = () => {
    if (!energyData.length) return null;

    const totalPower = energyData.reduce((sum, item) => sum + item.consumption.current, 0);
    const totalDaily = energyData.reduce((sum, item) => sum + item.consumption.daily, 0);
    const totalCost = energyData.reduce((sum, item) => sum + item.cost.daily, 0);
    const avgEfficiency = energyData.reduce((sum, item) => sum + item.efficiency.rating, 0) / energyData.length;
    const onlineCount = energyData.length; // 假設所有設備都在線
    const alertCount = alertsData.length;

    return { totalPower, totalDaily, totalCost, avgEfficiency, onlineCount, alertCount };
  };

  useEffect(() => {
    loadEnergyData();
  }, [filters]);

  const stats = calculateStats();
  const efficiencyInfo = stats ? getEfficiencyLevel(stats.avgEfficiency) : null;

  return (
    <div className="energy-management">
      {/* 過濾器和控制 */}
      <Card style={{ marginBottom: 16 }}>
        <Row gutter={16} align="middle">
          <Col span={4}>
            <Select
              placeholder="選擇區域"
              value={filters.area}
              onChange={(value) => setFilters(prev => ({ ...prev, area: value }))}
              allowClear
              style={{ width: '100%' }}
            >
              <Option value="A區">A區</Option>
              <Option value="B區">B區</Option>
              <Option value="C區">C區</Option>
              <Option value="地下停車場">地下停車場</Option>
            </Select>
          </Col>
          <Col span={4}>
            <Select
              placeholder="選擇樓層"
              value={filters.floor}
              onChange={(value) => setFilters(prev => ({ ...prev, floor: value }))}
              allowClear
              style={{ width: '100%' }}
            >
              <Option value={-1}>B1</Option>
              <Option value={1}>1F</Option>
              <Option value={2}>2F</Option>
              <Option value={3}>3F</Option>
            </Select>
          </Col>
          <Col span={4}>
            <Select
              value={filters.period}
              onChange={(value) => setFilters(prev => ({ ...prev, period: value }))}
              style={{ width: '100%' }}
            >
              <Option value="hourly">每小時</Option>
              <Option value="daily">每日</Option>
              <Option value="weekly">每週</Option>
              <Option value="monthly">每月</Option>
            </Select>
          </Col>
          <Col span={4}>
            <Button
              type="primary"
              icon={<ExperimentOutlined />}
              onClick={handleAIOptimization}
              loading={aiOptimizing}
              style={{ width: '100%' }}
            >
              AI 優化
            </Button>
          </Col>
          <Col span={4}>
            {stats && stats.alertCount > 0 && (
              <Alert
                message={`${stats.alertCount} 個能源警報`}
                type="warning"
                showIcon
                style={{ margin: 0 }}
              />
            )}
          </Col>
          <Col span={4}>
            <Button
              type="primary"
              icon={<ReloadOutlined />}
              onClick={loadEnergyData}
              loading={loading}
              style={{ width: '100%' }}
            >
              刷新
            </Button>
          </Col>
        </Row>
      </Card>

      {/* 統計概覽 */}
      {stats && (
        <Row gutter={16} style={{ marginBottom: 24 }}>
          <Col span={6}>
            <Card>
              <Statistic
                title="總功率"
                value={stats.totalPower}
                precision={2}
                valueStyle={{ color: '#1890ff' }}
                prefix={<ThunderboltOutlined />}
                suffix="kW"
              />
              <div style={{ marginTop: 8 }}>
                <Progress
                  percent={(stats.totalPower / 1000) * 100}
                  strokeColor="#1890ff"
                  showInfo={false}
                  size="small"
                />
                <div style={{ fontSize: 12, color: '#666', marginTop: 4 }}>
                  在線設備: {stats.onlineCount} / {energyData.length}
                </div>
              </div>
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic
                title="日消耗"
                value={stats.totalDaily}
                precision={2}
                valueStyle={{ color: '#52c41a' }}
                suffix="kWh"
              />
              <div style={{ marginTop: 8, fontSize: 12, color: '#666' }}>
                預估月消耗: {(stats.totalDaily * 30).toFixed(2)} kWh
              </div>
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic
                title="日成本"
                value={stats.totalCost}
                precision={2}
                valueStyle={{ color: '#fa8c16' }}
                prefix="¥"
              />
              <div style={{ marginTop: 8, fontSize: 12, color: '#666' }}>
                預估月成本: ¥{(stats.totalCost * 30).toFixed(2)}
              </div>
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic
                title="平均效率"
                value={stats.avgEfficiency}
                precision={1}
                valueStyle={{ color: efficiencyInfo?.color }}
                prefix={<TrophyOutlined />}
                suffix="%"
              />
              <div style={{ marginTop: 8 }}>
                <Tag color={efficiencyInfo?.color}>{efficiencyInfo?.level}</Tag>
                <div style={{ fontSize: 12, color: '#666', marginTop: 4 }}>
                  {efficiencyInfo?.desc}
                </div>
              </div>
            </Card>
          </Col>
        </Row>
      )}

      {/* 圖表分析 */}
      <Tabs defaultActiveKey="trends" style={{ marginBottom: 16 }}>
        <TabPane tab="能耗趨勢" key="trends">
          <Card>
            {prepareTrendData().length > 0 ? (
              <Line
                data={prepareTrendData().flatMap(item => [
                  { time: item.time, value: item.consumption, category: '能耗 (kWh)' },
                  { time: item.time, value: item.cost, category: '成本 (元)' },
                  { time: item.time, value: item.efficiency, category: '效率 (%)' }
                ])}
                xField="time"
                yField="value"
                seriesField="category"
                smooth={true}
                color={['#1890ff', '#fa8c16', '#52c41a']}
                height={300}
                yAxis={{
                  title: {
                    text: '數值',
                  },
                }}
                legend={{
                  position: 'top',
                }}
              />
            ) : (
              <Empty description="暫無趨勢數據" />
            )}
          </Card>
        </TabPane>
        <TabPane tab="設備分布" key="distribution">
          <Row gutter={16}>
            <Col span={12}>
              <Card title="設備類型能耗分布">
                {prepareDeviceTypeData().length > 0 ? (
                  <Pie
                    data={prepareDeviceTypeData()}
                    angleField="consumption"
                    colorField="type"
                    radius={0.8}
                    height={250}
                    label={{
                      type: 'outer',
                      content: '{name}: {percentage}',
                    }}
                    interactions={[{ type: 'element-active' }]}
                  />
                ) : (
                  <Empty description="暫無分布數據" />
                )}
              </Card>
            </Col>
            <Col span={12}>
              <Card title="設備類型成本對比">
                {prepareDeviceTypeData().length > 0 ? (
                  <Column
                    data={prepareDeviceTypeData()}
                    xField="type"
                    yField="cost"
                    height={250}
                    color="#fa8c16"
                    label={{
                      position: 'middle',
                      style: {
                        fill: '#FFFFFF',
                        opacity: 0.6,
                      },
                    }}
                  />
                ) : (
                  <Empty description="暫無成本數據" />
                )}
              </Card>
            </Col>
          </Row>
        </TabPane>
      </Tabs>

      {/* 設備詳細數據 */}
      <Card title="能源設備監控">
        <Table
          columns={columns}
          dataSource={filteredData}
          rowKey="_id"
          loading={loading}
          pagination={{
            total: filteredData.length,
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => `第 ${range[0]}-${range[1]} 項，共 ${total} 項`
          }}
          locale={{
            emptyText: <Empty description="暫無能源設備數據" />
          }}
        />
      </Card>

      {/* 閾值設置模態框 */}
      <Modal
        title="設置能源警報閾值"
        open={thresholdModalVisible}
        onCancel={() => {
          setThresholdModalVisible(false);
          setSelectedDevice(null);
          form.resetFields();
        }}
        footer={null}
        width={500}
      >
        {selectedDevice && (
          <div>
            <div style={{ marginBottom: 16, padding: 16, background: '#f5f5f5', borderRadius: 6 }}>
              <p><strong>系統ID:</strong> {selectedDevice.systemId}</p>
              <p><strong>設備類型:</strong> 能源系統</p>
              <p><strong>位置:</strong> {selectedDevice.location.area} - {selectedDevice.location.floor}F</p>
              <p><strong>當前功率:</strong> {selectedDevice.consumption.current} kW</p>
              <p><strong>狀態:</strong> {getStatusTag('online')}</p>
            </div>

            <Form
              form={form}
              layout="vertical"
              onFinish={updateThresholds}
            >
              <Form.Item
                name="dailyLimit"
                label="日消耗限制 (kWh)"
                rules={[
                  { required: true, message: '請輸入日消耗限制' },
                  { type: 'number', min: 1, message: '限制值必須大於 0' }
                ]}
              >
                <InputNumber
                  min={1}
                  style={{ width: '100%' }}
                  placeholder="設置日消耗上限"
                />
              </Form.Item>

              <Form.Item
                name="monthlyLimit"
                label="月消耗限制 (kWh)"
                rules={[
                  { required: true, message: '請輸入月消耗限制' },
                  { type: 'number', min: 1, message: '限制值必須大於 0' }
                ]}
              >
                <InputNumber
                  min={1}
                  style={{ width: '100%' }}
                  placeholder="設置月消耗上限"
                />
              </Form.Item>

              <Form.Item
                name="efficiencyMin"
                label="最低效率要求 (%)"
                rules={[
                  { required: true, message: '請輸入最低效率要求' },
                  { type: 'number', min: 0, max: 100, message: '效率範圍: 0-100%' }
                ]}
              >
                <InputNumber
                  min={0}
                  max={100}
                  style={{ width: '100%' }}
                  placeholder="設置最低效率要求"
                />
              </Form.Item>

              <div style={{ marginBottom: 16, padding: 12, background: '#e6f7ff', borderRadius: 4 }}>
                <h4 style={{ margin: '0 0 8px 0', color: '#1890ff' }}>能效等級參考:</h4>
                <div style={{ fontSize: 12, lineHeight: '18px' }}>
                  <div>• A+ (90%+): 極高效率，節能表現優異</div>
                  <div>• A (80-89%): 高效率，節能效果良好</div>
                  <div>• B (70-79%): 良好效率，符合標準</div>
                  <div>• C (60-69%): 一般效率，有改善空間</div>
                  <div>• D (60%以下): 低效率，需要優化</div>
                </div>
              </div>

              <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
                <Space>
                  <Button onClick={() => {
                    setThresholdModalVisible(false);
                    setSelectedDevice(null);
                    form.resetFields();
                  }}>
                    取消
                  </Button>
                  <Button type="primary" htmlType="submit">
                    保存設置
                  </Button>
                </Space>
              </Form.Item>
            </Form>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default EnergyManagement;